import type { HierarchyRung } from '@/types/institution-templates';

/**
 * The geometry behind the hierarchy diagram — extracted from the SVG so the layout
 * is a pure function a test can hold still, exactly as provenance-ladder.ts does
 * for the studio's ladder.
 *
 * WHAT THE PICTURE CLAIMS. A template's hierarchy is a ladder: each rung contains
 * the one below it, top-down, and that containment is the entire semantics — there
 * is no branching between rungs. Auxiliary unit types are the exception: they hang
 * off the institution root as side-branches because that is what they are in the
 * model, units an institution MAY create anywhere without them being a step on the
 * ladder. Drawing them as rungs would claim a Research Centre sits between Campus
 * and Faculty, which is false.
 *
 * Optional rungs are part of the claim too: a dashed rung says "your institution
 * may skip this level", and the dash must sit on the RUNG, not the connector — the
 * connector below an optional rung still exists when the rung is skipped, because
 * the ladder closes over the gap.
 */

export interface DiagramRungNode {
  kind: 'rung';
  unitType: string;
  label: string;
  required: boolean;
  depth: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface DiagramAuxNode {
  kind: 'aux';
  unitType: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

export type DiagramNode = DiagramRungNode | DiagramAuxNode;

export interface DiagramEdge {
  /** 'ladder' joins rung to rung; 'branch' joins the root rung to an auxiliary unit. */
  kind: 'ladder' | 'branch';
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export interface HierarchyLayout {
  width: number;
  height: number;
  rungs: DiagramRungNode[];
  aux: DiagramAuxNode[];
  edges: DiagramEdge[];
}

/* Geometry constants. Kept here, not in the component, so the tests pin the same
 * numbers the SVG draws with. Widths assume IBM Plex Sans at the diagram's sizes. */
export const RUNG_WIDTH = 240;
export const RUNG_HEIGHT = 52;
export const RUNG_GAP = 26;
export const AUX_WIDTH = 190;
export const AUX_HEIGHT = 34;
export const AUX_GAP = 12;
export const MARGIN = 12;
/** Horizontal clearance between the ladder column and the auxiliary column. */
export const COLUMN_GAP = 56;

/**
 * Lays the ladder out top-down with auxiliary units as a right-hand column
 * branching from the root rung.
 *
 * The layout never reorders, drops or invents nodes: one rung node per hierarchy
 * entry in the order given (the resolver's order IS the containment order), one
 * aux node per auxiliary type. An empty hierarchy yields an empty diagram with
 * honest zero-ish dimensions rather than a decorative frame around nothing.
 */
export function layoutHierarchy(
  hierarchy: readonly HierarchyRung[],
  auxiliaryUnitTypes: readonly string[],
): HierarchyLayout {
  const rungX = MARGIN;
  const auxX = MARGIN + RUNG_WIDTH + COLUMN_GAP;

  const rungs: DiagramRungNode[] = hierarchy.map((rung, index) => ({
    kind: 'rung',
    unitType: rung.unitType,
    label: rung.label,
    required: rung.required,
    depth: index + 1,
    x: rungX,
    y: MARGIN + index * (RUNG_HEIGHT + RUNG_GAP),
    width: RUNG_WIDTH,
    height: RUNG_HEIGHT,
  }));

  // Auxiliary units hang beside the ladder, starting level with the root rung —
  // visually "attached to the institution", which is the only true statement the
  // model makes about where they belong.
  const aux: DiagramAuxNode[] = auxiliaryUnitTypes.map((unitType, index) => ({
    kind: 'aux',
    unitType,
    x: auxX,
    y: MARGIN + index * (AUX_HEIGHT + AUX_GAP),
    width: AUX_WIDTH,
    height: AUX_HEIGHT,
  }));

  const edges: DiagramEdge[] = [];

  // Ladder connectors: centre-bottom of each rung to centre-top of the next.
  for (let index = 0; index + 1 < rungs.length; index += 1) {
    const from = rungs[index];
    const to = rungs[index + 1];
    edges.push({
      kind: 'ladder',
      x1: from.x + from.width / 2,
      y1: from.y + from.height,
      x2: to.x + to.width / 2,
      y2: to.y,
    });
  }

  // Branches: right edge of the root rung to the left edge of each aux node.
  const root = rungs[0];
  if (root) {
    for (const node of aux) {
      edges.push({
        kind: 'branch',
        x1: root.x + root.width,
        y1: root.y + root.height / 2,
        x2: node.x,
        y2: node.y + node.height / 2,
      });
    }
  }

  const rungExtent = rungs.length
    ? rungs[rungs.length - 1].y + RUNG_HEIGHT
    : MARGIN;
  const auxExtent = aux.length ? aux[aux.length - 1].y + AUX_HEIGHT : MARGIN;

  return {
    width: aux.length > 0 ? auxX + AUX_WIDTH + MARGIN : MARGIN + RUNG_WIDTH + MARGIN,
    height: Math.max(rungExtent, auxExtent) + MARGIN,
    rungs,
    aux,
    edges,
  };
}

/**
 * COURSE_OFFERING → "Course offering" — a readable fallback for unit types the
 * translation file does not know. Same contract as the studio's humanizeValue:
 * server data made readable while staying recognisably the same token.
 */
export function humanizeUnitType(code: string): string {
  if (code.length === 0) return code;
  const lower = code.replace(/_/g, ' ').toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}
